import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ModuleConsoleFooter } from "@/components/composition/ModuleConsoleFooter";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { t } from "@/core/composition/lexicon";
import { buildScheduleRows, loadHospitalSchedules } from "@/core/rh/rh-api";
import type { HospitalScheduleRow } from "@/core/rh/types";
import { routes } from "@/router/all_routes";

const SHIFT_BADGE: Record<HospitalScheduleRow["shiftCode"], string> = {
  morning: "bg-info text-dark",
  afternoon: "bg-primary",
  night: "bg-dark",
  oncall: "bg-warning text-dark",
};

export function RhSchedulesPage() {
  const [rows, setRows] = useState<HospitalScheduleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState("");

  const [scheduleSource, setScheduleSource] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { rows, source } = await loadHospitalSchedules();
      if (cancelled) return;
      setRows(rows);
      setScheduleSource(source);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const departments = useMemo(
    () => [...new Set(rows.map((r) => r.department))].sort((a, b) => a.localeCompare(b, "fr")),
    [rows],
  );

  const filtered = useMemo(() => {
    if (!department) return rows;
    return rows.filter((r) => r.department === department);
  }, [rows, department]);

  const pagination = useClientPagination(filtered, [department], 25);
  const { pagedItems } = pagination;

  return (
    <div className="ph-dashboard">
      <PageHeader
        title={t("rh.schedules")}
        description="Plannings de garde — données RH API (fallback démo si hors ligne)."
        actions={
          <Link to={routes.rhEmployees} className="btn btn-outline-secondary btn-sm">
            {t("rh.employees")}
          </Link>
        }
      />

      <div className="ph-dash-card">
        <div className="d-flex flex-wrap gap-2 justify-content-between mb-3 ph-stock-list-head">
          <h3 className="h6 mb-0">Semaine en cours</h3>
          {scheduleSource && (
            <span className="badge text-bg-light border">
              Source : {scheduleSource === "api" ? "RH API" : "Démo"}
            </span>
          )}
          <select
            className="form-select form-select-sm w-100 w-md-auto"
            style={{ maxWidth: "14rem" }}
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          >
            <option value="">Tous les services</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-muted small mb-0">Chargement…</p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Service</th>
                  <th>Fonction</th>
                  <th>Créneau</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((row) => (
                  <tr key={row.staffId} className="ph-diag-row">
                    <td data-label="Agent" className="fw-medium">
                      {row.staffName}
                    </td>
                    <td data-label="Service">{row.department}</td>
                    <td data-label="Fonction" className="small">
                      {row.position}
                    </td>
                    <td data-label="Créneau">
                      <span className={`badge ${SHIFT_BADGE[row.shiftCode]}`}>
                        {row.shiftLabel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>

      <ModuleConsoleFooter consoleRoute={routes.rhAdminConsole} moduleLabel="ITaaS RH" />
    </div>
  );
}
